import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MultiValue } from 'react-select';
import { CustomSelect } from 'components';
import { useLazyGetUsersOptionListQuery } from 'api';
import { transformDataIntoOptionType } from 'common/helpers/v1';
import debounce from 'lodash/debounce';
import { OptionType } from 'common/components/DropdownOption';
import { UserTypes } from 'types/user-management';
import styled from 'styled-components';
import { transfromUserOptions } from 'pages/UserManagementTwo/components/utils';

const UserFormContainer = styled.div`
  margin: 1rem 0;
  .custom-select {
    margin: 1.5rem 0;
  }
`;
type UserOption = {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

type Props = {
  handleUserSelect: (users: UserTypes[]) => void;
  existingGroupUsers?: UserTypes[];
  addedGroupUsers?: string[];
  showMultiDropdown?: boolean;
};
const USER_API_LIMIT = 10;

const AddExistingUser = ({
  handleUserSelect,
  existingGroupUsers,
  addedGroupUsers = [],
  showMultiDropdown
}: Props): JSX.Element | null => {
  const [usersList, setUsersList] = useState<UserTypes[]>([]); // Current options
  const [selectedOptions, setSelectedOptions] = useState<MultiValue<OptionType>>(); // Selected options
  const [offset, setOffset] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [hasMore, setHasMore] = useState<boolean>(true); // Whether more options are available

  const [getUser, { isLoading, isFetching }] = useLazyGetUsersOptionListQuery();

  const getUsers = async ({
    userName = inputValue,
    offset
  }: {
    userName: string;
    offset: number;
  }) => {
    const response = await getUser({
      limit: USER_API_LIMIT,
      offset: offset,
      name_search_query: userName
    });

    const { total, items } = response.data as { total: number; items: UserTypes[] };
    setUsersList(offset ? [...usersList, ...items] : items);
    setHasMore(usersList.length + items.length < total);
  };

  // Load more options
  const loadMoreUsers = async () => {
    if (isLoading || !hasMore) return;
    setOffset(offset + 1);
  };

  // Initial load
  useEffect(() => {
    getUsers({
      userName: inputValue,
      offset
    });
  }, [offset, addedGroupUsers]);

  useEffect(() => {
    const user = transformDataIntoOptionType<UserTypes>(
      existingGroupUsers || [],
      (user) => `${user.firstName} ${user.lastName}`,
      (user) => user,
      (user) => addedGroupUsers.includes(user.id || '')
    ) as OptionType[];
    setSelectedOptions(user);
  }, []);

  // Handle multi selection change
  const handleMultiUserSelect = (selected: unknown) => {
    const selectedUsers = selected as UserOption[];
    setSelectedOptions(selectedUsers);
    const users = selectedUsers.map((userOption: UserOption) => ({
      ...userOption.value,
      text_alert: false,
      email_alert: false
    }));
    handleUserSelect(users);
    setInputValue(inputValue);
  };

  // Debounced function to handle input change
  const updateDebouncedValue = useCallback(
    debounce((value) => {
      getUsers({
        userName: value || inputValue,
        offset: 0
      });
    }, 300),
    [inputValue]
  );

  // Handle input change
  const handleChange = (value: string) => {
    setInputValue(value); // Updates the immediate state
    updateDebouncedValue(value); // Triggers the debounced update
  };

  const userOptions = useMemo(() => transfromUserOptions(usersList, addedGroupUsers), [usersList]);

  return (
    <UserFormContainer>
      {showMultiDropdown || selectedOptions?.length ? (
        <CustomSelect
          id="multi-user"
          isMulti={true}
          hideSelectedOptions={false}
          isCheckboxInput={true}
          onMenuScrollToBottom={loadMoreUsers} // Trigger when scrolling to the bottom
          mandatory
          // label="Add a User"
          placeholder={'Add an existing User'}
          value={selectedOptions}
          options={userOptions}
          onChange={handleMultiUserSelect}
          isLoading={isLoading || isFetching}
          onInputChange={handleChange}
          inputValue={inputValue}
          customMenu
        />
      ) : (
        <></>
      )}
    </UserFormContainer>
  );
};

export default AddExistingUser;
